import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { useWriteEnableStore } from '@/stores/writeEnableStore';
import { useBrowserStore } from '@/stores/browserStore';
import { ZOOM_SIGNIN_URL } from '@/lib/zoomSetup';
import { client } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Guided write-access enablement, rendered inside the Zoom setup chat when a
 * plan needed write access, or scopes the app lacks. Shows a mini step-tracker
 * (Sign in → Add the scopes → Activate → Verify) while the harness works in
 * the visible browser panel, then opens the plan to approve it.
 */
export function WriteEnablePanel() {
  const navigate = useNavigate();
  const phase = useWriteEnableStore((s) => s.phase);
  const pendingPlan = useWriteEnableStore((s) => s.pendingPlan);
  const signinNeeded = useWriteEnableStore((s) => s.signinNeeded);
  const error = useWriteEnableStore((s) => s.error);
  const run = useWriteEnableStore((s) => s.run);
  const reset = useWriteEnableStore((s) => s.reset);
  const zoomSignedIn = useBrowserStore((s) => s.zoomSignedIn);
  const setPendingNavigateUrl = useBrowserStore((s) => s.setPendingNavigateUrl);

  // When sign-in is required, point the panel at Zoom's sign-in surface.
  useEffect(() => {
    if (signinNeeded) setPendingNavigateUrl(ZOOM_SIGNIN_URL);
  }, [signinNeeded, setPendingNavigateUrl]);

  // Whether the person is signed in is read off the harness tab itself (the setup's own probe): the flag the account
  // call reports is the old local Chrome's, never set with the harness (live 2026-11-03: signed in, and the panel waited
  // for ever). Two signed-in reads in a row, as the setup's sign-in detection takes it.
  useEffect(() => {
    if (!signinNeeded || zoomSignedIn) return;
    let cancelled = false;
    let polling = false;
    let streak = 0;
    const poll = async () => {
      if (polling) return;
      polling = true;
      try {
        const state = await client.browser.getZoomAuthState();
        if (cancelled || !state.enabled) return;
        streak = state.signedIn ? streak + 1 : 0;
        if (streak >= 2) useBrowserStore.getState().setZoomSignedIn(true);
      } catch {
        // The tab may be between pages: read again on the next tick.
      } finally {
        polling = false;
      }
    };
    const timer = setInterval(() => { void poll(); }, 2500);
    void poll();
    return () => { cancelled = true; clearInterval(timer); };
  }, [signinNeeded, zoomSignedIn]);

  // Once the user signs in (harness reports it), auto-retry enablement.
  // Once a sign-in phase: a retry that asks for sign-in again waits for the person (Retry), never retries by itself.
  const retriedRef = useRef(false);
  useEffect(() => {
    if (signinNeeded && zoomSignedIn && !retriedRef.current) {
      retriedRef.current = true;
      void run();
    }
    if (phase === 'idle' || phase === 'done') retriedRef.current = false;
  }, [signinNeeded, zoomSignedIn, run, phase]);

  // Done. Launched from a plan (pendingPlan, D3, D7): open it, now approvable. Otherwise stay here.
  useEffect(() => {
    if (phase !== 'done') return;
    const t = setTimeout(() => {
      const store = useWriteEnableStore.getState();
      const plan = store.pendingPlan;
      reset();
      if (plan) {
        store.clearPendingPlan();
        navigate(`/changes/${plan.planId}`);
      }
    }, 900);
    return () => clearTimeout(t);
  }, [phase, reset, navigate]);

  const steps = useMemo(() => {
    const signinDone = !signinNeeded || zoomSignedIn;
    const scopesActive = phase === 'scopes';
    const terminalDone = phase === 'done';
    return [
      // Sign-in only shows as its own step when it was actually required.
      ...(signinNeeded ? [{ label: 'Sign in to Zoom', done: signinDone, active: signinNeeded && !zoomSignedIn }] : []),
      { label: 'Add write scopes', done: terminalDone, active: scopesActive },
      { label: 'Activate app', done: terminalDone, active: false },
      { label: 'Verify write access', done: terminalDone, active: false },
    ];
  }, [phase, signinNeeded, zoomSignedIn]);

  return (
    <div className="mx-4 mt-1 rounded-lg border border-primary/30 bg-card/70 px-4 py-3">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Enabling write access</span>
      </div>

      <ol className="mt-3 space-y-1">
        {steps.map((step) => (
          <li key={step.label} className="flex items-center gap-2 text-xs">
            {step.done ? (
              <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500/20">
                <Check className="h-2.5 w-2.5 text-emerald-400" />
              </span>
            ) : step.active ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-400" />
            ) : (
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <span className="h-2 w-2 rounded-full border border-muted-foreground/40" />
              </span>
            )}
            <span className={cn(step.done && 'text-muted-foreground line-through', step.active && 'font-medium text-foreground', !step.done && !step.active && 'text-muted-foreground')}>
              {step.label}
            </span>
          </li>
        ))}
      </ol>

      {phase === 'signin' && (
        <div className="mt-2">
          <p className="text-xs text-amber-400/90">Sign in to Zoom in the browser panel — I'll continue automatically.</p>
          {retriedRef.current && (
            <button onClick={() => void run()} className="mt-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90">
              Retry
            </button>
          )}
        </div>
      )}
      {phase === 'done' && (
        <p className="mt-2 text-xs text-emerald-400">
          {pendingPlan ? 'Write access enabled — taking you to your plan to approve it…' : 'Write access enabled.'}
        </p>
      )}
      {phase === 'error' && (
        <div className="mt-2">
          <p className="flex items-center gap-1.5 text-xs text-red-400"><AlertCircle className="h-3.5 w-3.5" />{error}</p>
          <button onClick={() => void run()} className="mt-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90">
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
